// The registrar's reference for the health module, living where the tools
// live. Content mirrors the working guide artifact (2026-07-06); keep the two
// in step when behaviour changes. Admin-gated: it documents admin surfaces.

import type { JSX, ReactNode } from 'react';
import { useHealthFrame } from './HealthLayout';
import { GuideWalkthrough } from './GuideWalkthrough';

const Chip = ({ children, primary }: { children: ReactNode; primary?: boolean }): JSX.Element => (
  <span className={`guide-chip${primary ? ' guide-chip-primary' : ''}`}>{children}</span>
);

const Tag = ({ kind }: { kind: 'safeguarding' | 'governance' }): JSX.Element => (
  <span className={`pill ${kind === 'safeguarding' ? 'pill-danger' : 'pill-info'}`}>
    {kind === 'safeguarding' ? 'Safeguarding' : 'Governance'}
  </span>
);

function Card({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <div className="card">
      <h2 className="health-section-heading">
        <span>{title}</span>
      </h2>
      <div className="guide-body">{children}</div>
    </div>
  );
}

export function GuidePage(): JSX.Element {
  const { role } = useHealthFrame();
  if (role !== 'admin') {
    return (
      <div className="card" style={{ maxWidth: 560, margin: '30px auto', textAlign: 'center' }}>
        <p style={{ fontSize: 14, color: 'var(--health-grey)', lineHeight: 1.7 }}>
          The guide covers the Registrar's administration tools. If you facilitate an assessment and
          have questions, please contact registrar@confessinganglicans.nz.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 16, maxWidth: 760 }}>
      <div>
        <h1 style={{ fontSize: 22, marginBottom: 4 }}>How this module works</h1>
        <p style={{ fontSize: 13.5, color: 'var(--health-grey)', lineHeight: 1.7 }}>
          Take the guided tour, or read the full reference below — launching assessments, editing
          instruments, safeguarding and governance markings, copying and ingesting documents, and
          what every setting does.
        </p>
      </div>

      <GuideWalkthrough />

      <div
        style={{
          marginTop: 8,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.09em',
          textTransform: 'uppercase',
          color: 'var(--health-accent)',
        }}
      >
        The full reference
      </div>

      <Card title="Launching an assessment">
        <p>
          Assessments are launched from <strong>Assessments &rsaquo; New assessment</strong>. The
          CRM record is written for you.
        </p>
        <ol className="guide-steps">
          <li>
            Pick the church, the window (links only work from the opening day, NZ time, and stay
            open through the whole closing day), the mode, and the instrument version. The defaults
            come from Settings.
          </li>
          <li>
            Give the facilitator's name and email and press <Chip>Check</Chip>. If the email has no
            CRM contact, or the contact lacks the Facilitator role, a tick-box offers to fix that on
            the spot; without it they cannot sign in to facilitate.
          </li>
          <li>
            Press <Chip primary>Create and choose invitees</Chip>. The church's leaders load from
            the CRM as a tick-to-invite list; adjust roles, add anyone missing, then{' '}
            <Chip primary>Add and send invitations</Chip> or <Chip>Save for later</Chip>. Everything
            on this step can also be done later from the assessment's Invitations tab.
          </li>
        </ol>
      </Card>

      <Card title="The one rule underneath everything">
        <p>
          You always work on a <strong>draft</strong> — nothing a respondent sees changes until you
          commit it, and the editor commits it the safest way for what you changed:
        </p>
        <ul className="guide-list">
          <li>
            a <strong>wording</strong> fix saves onto the same version, in place;
          </li>
          <li>
            <strong>adding</strong> a question saves onto the same version too, after a
            confirmation;
          </li>
          <li>
            <strong>removing or reordering</strong> questions is published as a{' '}
            <strong>new version</strong>, kept separate from the one in use.
          </li>
        </ul>
        <p className="guide-callout">
          The rule underneath never bends:{' '}
          <strong>answers people have already given are never put at risk</strong>. That is the
          whole reason the one change that could misalign them — removing or reordering questions on
          a version already in use — becomes a new version instead of an edit in place.
        </p>
        <p className="guide-muted">
          Drafts are also saved on the device as you type; a banner offers to restore an interrupted
          edit next time you open the editor.
        </p>
      </Card>

      <Card title="Getting around the instrument editor">
        <p>
          The toolbar above the Basis card sets what you are working on: the <Chip>Instrument</Chip>{' '}
          picker chooses which named instrument, and <Chip>Editing from</Chip> chooses which
          published version your draft starts from. <Chip>Rename</Chip> changes an instrument's
          catalogue name only — not its versions or their questions.
        </p>
        <p>
          Switching instrument or version discards the current draft (it asks first) — which is also
          the quickest way to start over after a mess.
        </p>
      </Card>

      <Card title="Editing text and questions">
        <p>
          Click any text (title, aim, question, scale wording) and type. Select some text for the
          formatting bar, or use <kbd>⌘B</kbd> <kbd>⌘I</kbd> <kbd>⌘U</kbd>.
        </p>
        <ul className="guide-list">
          <li>
            <Chip>↑</Chip> <Chip>↓</Chip> reorder within the section; <Chip>Move to...</Chip> sends
            a question (with its follow-ups) to another section; <Chip>+ Follow-up</Chip> adds an
            indented written question beneath; <Chip>✕</Chip> deletes (it asks first).
          </li>
          <li>
            The <em>group label</em> box is presentation only: its text becomes a small heading
            above the question in the survey, and consecutive questions with the same label sit
            together under it. It never affects scoring or analysis.
          </li>
          <li>
            The <Chip>Follows section (...)</Chip> dropdown is that question's
            safeguarding/governance override — see the markings card below.
          </li>
        </ul>
        <p>
          Section cards carry the same controls plus <Chip>Preview</Chip>;{' '}
          <Chip>+ Add a section</Chip> sits at the bottom of the page, and{' '}
          <Chip>Preview the whole survey</Chip> at the top walks everything, including unpublished
          changes.
        </p>
      </Card>

      <Card title="Safeguarding and governance markings">
        <p>
          <strong>Why this exists:</strong> results are mostly averages, and averages hide rare bad
          answers. If nine leaders score a child-safety question 4 and one scores it 1, the average
          of 3.7 looks healthy — yet that one answer is the most important thing in the assessment.
          Marked questions are exempted from averaging so that can never happen.
        </p>
        <p>
          Each section has a <strong>Designation</strong> select under its aim.{' '}
          <Tag kind="safeguarding" /> questions are always shown one by one in their own results
          panel and are red-flagged the moment any single person answers 1 or 2 (or the average
          falls below 3). <Tag kind="governance" /> questions (policies, procedures, compliance) are
          also shown one by one, but never alarm-styled: an out-of-date policy is a task, not a
          crisis, and flagging it would cheapen the safeguarding alarm. Unmarked questions feed
          their section's average normally.
        </p>
        <p>
          Individual questions override the section with their own{' '}
          <Chip>Follows section (...)</Chip> dropdown: inherit (the default), Safeguarding,
          Governance, or <em>Not designated</em> to opt out of a designated section. Section 12 is
          the live example: the section is Governance, with the Safe Ministry Blueprint and VPSO
          questions individually marked Safeguarding. Where a question would be both, safeguarding
          wins.
        </p>
        <p className="guide-callout">
          When a version has no markings yet, the editor offers to carry them in from the module
          settings (a button). Because that changes designations, it is published as a new version,
          not corrected in place — and from that version on, the markings travel inside the
          instrument itself.
        </p>
      </Card>

      <Card title="Copy from another instrument">
        <ol className="guide-steps">
          <li>
            Press <Chip>Copy from another instrument</Chip> at the top of the editor.
          </li>
          <li>Pick the source instrument and version; its sections appear below.</li>
          <li>
            Tick a <strong>whole section</strong> (added to the end of your draft), or tick{' '}
            <strong>individual questions</strong> and choose which of your sections they join.
          </li>
          <li>
            Press <Chip primary>Copy into the draft</Chip>.
          </li>
        </ol>
        <p>
          Follow-ups travel with their parent; safeguarding and governance markings keep their
          source meaning. Copies are independent: a copied question is a new question — editing
          either side never affects the other, and its answer history starts fresh.
        </p>
      </Card>

      <Card title="Ingest a document">
        <ol className="guide-steps">
          <li>
            Open the document (Word, Google Docs, PDF, an email) and copy its text; plain-text files
            can be attached directly.
          </li>
          <li>
            Press <Chip>Ingest a document</Chip> and paste everything in.
          </li>
          <li>
            Press <Chip primary>Ingest into a draft</Chip> and keep the window open — a full
            instrument takes a minute or two to structure.
          </li>
          <li>
            The result lands as a normal draft — sections, scale and written questions, follow-ups,
            and any safeguarding or governance markings it recognised — with the publish panel
            pre-set to a new instrument named from the document's title.
          </li>
          <li>
            <strong>Check every section against the original.</strong> It is faithful but not
            infallible. Nothing is published until you publish it.
          </li>
        </ol>
        <p className="guide-callout guide-callout-warn">
          Ingesting a document replaces the current draft (it asks first). Publish or discard
          anything you care about beforehand.
        </p>
      </Card>

      <Card title="Publishing a new version">
        <p>
          Publishing has two targets: <strong>into the current instrument</strong> (a new version in
          its history) or <strong>as a new instrument named ...</strong> (the duplicate flow). The
          version number is suggested automatically and can only ever be published once.
        </p>
        <p>
          <strong>
            A new version does not move any assessment that is already using the old one.
          </strong>{' '}
          Running and closed assessments keep the exact version they started with — that is how
          their answers stay comparable. Ticking{' '}
          <em>"Use this version for newly created cycles"</em> makes it the default for{' '}
          <em>future</em> assessments only.
        </p>
        <p>
          To put a new version into an assessment that already exists, open it and choose the
          version on its <strong>Cycle settings</strong> tab — possible only while it is open and no
          one has responded yet. (This is also how a version that came from a removing-or-reordering
          edit reaches a running assessment: publish it, then switch to it there.)
        </p>
      </Card>

      <Card title="Module settings (the Settings tab)">
        <p>
          Defaults that govern every assessment; individual assessments override mode, reminders,
          and dates on their own Cycle settings tab. Nothing applies until{' '}
          <Chip primary>Save settings</Chip> is pressed — and Save writes exactly what is in the
          boxes, empty included.
        </p>
        <ul className="guide-list">
          <li>
            <strong>Module switch</strong> — the master switch: off means every survey link shows a
            closed message and no emails send at all.
          </li>
          <li>
            <strong>Default cycle mode</strong> — what new assessments start as. Rostered: named
            invite list, personal links, per-person reminders. Open link: one shared link, personal
            links minted on arrival.
          </li>
          <li>
            <strong>Automatic reminders</strong> — on: a daily job nudges non-responders within the
            reminder limits; off: reminders are manual from the Invitations tab.
          </li>
          <li>
            <strong>Edit re-authentication</strong> — not yet functional (the PIN step it governs
            has not been built); leave off.
          </li>
          <li>
            <strong>Sender / reply-to</strong> — what the module's emails send as, and where replies
            land.
          </li>
          <li>
            <strong>Reminder limits</strong> — no one is reminded more often than every N days, or
            more than M times per assessment.
          </li>
          <li>
            <strong>Anonymity thresholds</strong> — the suppression tiers: minimum submissions
            before any results show; before written answers show; per role bucket before a role
            breakdown; and before a divergence flag. They are the no-one-can-be-picked-out promise —
            raise with care, never lower casually.
          </li>
          <li>
            <strong>Safeguarding / Governance lists</strong> — fallback designations for versions
            published before designations lived in the instrument. Sections (s8) or single questions
            (s12c1); safeguarding wins overlaps. Versions with their own markings ignore these
            fields.
          </li>
          <li>
            <strong>Written-answer retention</strong> — written answers purge this many days after
            close (scores are kept); manual purge sits on each debriefed cycle.
          </li>
          <li>
            <strong>Operations and audit</strong> — recent email failures and bounces, the
            suppression list (sends always skip it), and the audit trail of every admin action.
          </li>
        </ul>
      </Card>

      <Card title="Quick answers">
        <div className="guide-qa">
          <h3>Can I fix a typo in a published version?</h3>
          <p className="guide-muted">
            Yes. Load the version in <Chip>Editing from</Chip>, fix the wording, and the editor
            offers <Chip>Correct wording</Chip> — it saves onto the same version in place (you see
            the before→after first). Adding a question is also allowed, behind a confirmation. Only
            removing or reordering questions needs a new version, because that can misalign answers
            already collected.
          </p>
          <h3>I have made a mess of the draft.</h3>
          <p className="guide-muted">
            Re-choose the version in <Chip>Editing from</Chip> (or switch instruments and back). The
            draft is discarded after a confirmation.
          </p>
          <h3>Can I add safeguarding markings to an older version?</h3>
          <p className="guide-muted">
            When a version has none, the editor offers to carry them in from the module settings.
            Because changing designations affects analysis, that is published as a new version
            rather than corrected in place.
          </p>
          <h3>How often can I ingest documents?</h3>
          <p className="guide-muted">Six an hour; the button tells you if you hit the limit.</p>
        </div>
      </Card>
    </div>
  );
}
